import { Link } from "react-router-dom";
import { LINKED_PLACES } from "../data/places";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { Facebook, Instagram } from "lucide-react";
import { Editable } from "./edit/Editable";
import { EditEntityButton } from "./edit/EntityEditor";
import { useContent } from "../context/ContentContext";
import { useCart } from "../context/CartContext";

const SOCIALS = [
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61592444702280", icon: <Facebook size={18} /> },
  { label: "Instagram", href: "https://www.instagram.com/urbanturbanchicago/", icon: <Instagram size={18} /> },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@urbanturban2660",
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298 0 .595.047.88.14V9.4a6.33 6.33 0 0 0-1-.08A6.34 6.34 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
      </svg>
    ),
  },
  {
    label: "X",
    href: "https://x.com/UrbanTurbanChi",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.47l8.6-9.83L0 1.15h7.59l5.24 6.93 6.07-6.93zm-1.29 19.5h2.04L6.49 3.24H4.3l13.31 17.41z" />
      </svg>
    ),
  },
];

const FOOTER_PLACES = LINKED_PLACES;

export const Footer = () => {
  const [locations, setLocations] = useState([]);
  const [customPages, setCustomPages] = useState([]);
  const { editMode } = useContent();
  const { publicBrands } = useCart();
  const reloadLocations = () => api.get("/locations").then(({ data }) => setLocations(data)).catch(() => {});
  useEffect(() => {
    reloadLocations();
    api.get("/cms/pages").then(({ data }) => setCustomPages(data.filter((p) => p.show_in_footer))).catch(() => {});
  }, []);
  const FL = ({ to, k, fallback, testId }) => (
    <Link to={to} data-testid={testId} className="text-neutral-400 hover:text-white transition-colors" onClick={(e) => { if (editMode) e.preventDefault(); }}><Editable k={k} fallback={fallback} label={`Footer link: ${fallback}`} /></Link>
  );
  return (
  <footer className="mt-24 ut-footer" style={{ background: "var(--ut-panel)", color: "var(--ut-ink)", borderTop: "1px solid var(--ut-line)" }} data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid gap-10 md:grid-cols-3">
      <div>
        <img src="/logo-horizontal.webp" alt="Urban Turban by VeerGi" width="640" height="219" className="h-10 w-auto" loading="lazy" />
        <p className="text-sm text-neutral-300 mt-3" data-testid="footer-slogan">Indian handi cooking. Punjabi roots.</p>
        <Editable as="p" k="footer.tagline" fallback="Punjabi restaurant, quick bites and fine gold — one brand, every craving." label="Footer tagline" multiline className="text-sm text-neutral-400 mt-4 max-w-xs" />
        <div className="mt-5 text-sm" data-testid="footer-hours">
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-neutral-500 mb-3">Locations & Hours</p>
          <div className="space-y-4">
            {locations.filter((loc) => loc.listed !== false).map((loc) => (
              <div key={loc.id} data-testid={`footer-loc-${loc.id}`} className="relative">
                <EditEntityButton type="location" entity={loc} label="Edit hours/address" className="absolute -top-1 right-0" onSaved={reloadLocations} />
                <p className="text-neutral-200 font-semibold text-[13px]">
                  {loc.name}
                  {loc.status === "temporarily_closed" && <span className="ml-2 px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 text-[9px] font-bold uppercase align-middle">Temporarily closed</span>}
                  {loc.status === "under_renovation" && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[9px] font-bold uppercase align-middle">Under renovation</span>}
                  {loc.status === "coming_soon" && <span className="ml-2 px-1.5 py-0.5 rounded bg-white/10 text-neutral-400 text-[9px] font-bold uppercase align-middle">Coming soon</span>}
                </p>
                {loc.address && loc.address !== "Coming soon" && <p className="text-neutral-500 text-xs">{loc.address}</p>}
                {loc.status === "active" && (loc.weekly || []).map((w) => (
                  <p key={w.day} className="text-[11px] text-neutral-400 leading-5"><span className="inline-block w-9 text-neutral-500">{w.day}</span>{w.label}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <FL to="/store/restaurant" testId="footer-link-turban" k="nav.restaurant" fallback="Urban Turban" />
        {publicBrands.filter((b) => b.slug !== "restaurant").map((b) => (
          <FL key={b.slug} to={`/store/${b.slug}`} testId={`footer-link-${b.slug}`} k={`nav.${b.slug}`} fallback={b.name} />
        ))}
        <a href="https://regaljewels.com" target="_blank" rel="noopener noreferrer" data-testid="footer-link-gold" className="text-neutral-400 hover:text-white transition-colors">Urban Gold & Diamonds</a>
        <FL to="/club" testId="footer-link-club" k="footer.link.club" fallback="Urban Club" />
        <FL to="/referrals" testId="footer-link-referrals" k="footer.link.referrals" fallback="Refer & Earn" />
        <FL to="/catering" testId="footer-link-catering" k="footer.link.catering" fallback="Catering" />
        <FL to="/store/halwai" testId="footer-link-halwai-mithai" k="footer.link.halwai_mithai" fallback="Urban Halwai Mithai Boxes" />
        <FL to="/catering#private-events" testId="footer-link-private-events" k="footer.link.private_events" fallback="Private events" />
        <FL to="/dietary" testId="footer-link-dietary" k="footer.link.dietary" fallback="Dietary info" />
        <FL to="/orders" testId="footer-link-orders" k="footer.link.orders" fallback="My Orders" />
        <FL to="/locations/halsted-chicago" testId="footer-link-loc-halsted" k="footer.link.halsted" fallback="Halsted — Chicago" />
        {customPages.map((p) => <Link key={p.id} to={`/p/${p.slug}`} data-testid={`footer-page-${p.slug}`} className="text-neutral-400 hover:text-white transition-colors">{p.title}</Link>)}
      </div>
      <div className="md:justify-self-end">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-neutral-500 mb-3">Follow us</p>
        <div className="flex items-center gap-3">
          {SOCIALS.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
              data-testid={`footer-social-${s.label.toLowerCase()}`}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#E04F33] flex items-center justify-center transition-colors">
              {s.icon}
            </a>
          ))}
        </div>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-neutral-500 mb-3">Find us near you</p>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs" data-testid="footer-city-links">
          {FOOTER_PLACES.map((pl) => (
            <Link key={pl.slug} to={`/places/${pl.slug}`} className="text-neutral-400 hover:text-white transition-colors">{pl.footerLabel || `Indian food ${pl.name}`}</Link>
          ))}
          <Link to="/indian-catering-chicago" className="text-neutral-400 hover:text-white transition-colors">Indian catering Chicago</Link>
          <Link to="/indian-street-food-chicago" className="text-neutral-400 hover:text-white transition-colors">Indian street food Chicago</Link>
          <Link to="/page/dietary-options" className="text-neutral-400 hover:text-white transition-colors">Halal, vegetarian &amp; vegan</Link>
          <Link to="/our-story" data-testid="footer-link-our-story" className="text-neutral-400 hover:text-white transition-colors">Our Story</Link>
          <Link to="/reservations" data-testid="footer-link-reservations" className="text-neutral-400 hover:text-white transition-colors">Reservations</Link>
          <Link to="/faq" data-testid="footer-link-faq" className="text-neutral-400 hover:text-white transition-colors">FAQ</Link>
          <Link to="/music" data-testid="footer-link-music" className="text-neutral-500 hover:text-[#e0a458] transition-colors italic">Music by VeerGi</Link>
        </div>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-neutral-500">© {new Date().getFullYear()} Urban Turban by VeerGi · Chicago</p>
        <p className="flex flex-wrap gap-x-4 text-xs text-neutral-500" data-testid="footer-legal-links">
          <a href="/giveaway" data-testid="footer-link-giveaway" className="hover:text-white transition-colors">Giveaway Rules</a>
          <a href="/privacy" data-testid="footer-link-privacy" className="hover:text-white transition-colors">Privacy</a>
          <a href="/terms" data-testid="footer-link-terms" className="hover:text-white transition-colors">Terms</a>
          <a href="/data-deletion" data-testid="footer-link-data-deletion" className="hover:text-white transition-colors">Data deletion</a>
          <a href="https://search.google.com/local/writereview?placeid=ChIJF0h9C-vTD4gRRBMrKMxTkFU" target="_blank" rel="noopener" data-testid="footer-review-link" className="hover:text-white transition-colors">Review us on Google</a>
          <Link to="/login" data-testid="footer-link-staff-login" className="hover:text-white transition-colors">Staff &amp; Club sign-in</Link>
        </p>
        <p className="flex items-center gap-2 text-neutral-400" data-testid="footer-group-brand">
          <img src="/brand/urban-by-veergi.png" alt="URBAN by VeerGi" width="600" height="646" className="h-6 w-auto opacity-90" loading="lazy" /> Urban Turban is part of URBAN by VeerGi
        </p>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
